import { Component, DestroyRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { Auth } from '@angular/fire/auth';
import { IonModal } from '@ionic/angular';
import { filter } from 'rxjs/operators';

import {
  HOME_ASSETS,
  HOME_BENEFITS,
  HOME_BENEFITS_BUTTON,
  HOME_BENEFITS_TITLE,
  HOME_CTA,
  HOME_GREETING,
  HOME_HIGHLIGHTS,
  HOME_LINKS,
  HOME_SUBSCRIPTION_URL,
  HOME_VIDEO,
  IHomeHighlight,
} from '../../constants/home.constant';
import { toAssetUrl, toYoutubeEmbedUrl } from '../../../core/utils/functions';
import { GlobalStorage } from '../../../core/services/globals.service';
import {
  ApvConfigService,
  SHOW_GENERATIONAL_FUNDS_DEFAULT,
} from '../../../core/services/firestore/apv-config.service';
import { SubscriptionStore } from '../../../subscription/services/subscription.store';
@Component({
    selector: 'app-home-page',
    templateUrl: './home.page.html',
    styleUrls: ['./home.page.scss'],
    standalone: false
})
export class HomePage implements OnInit, OnDestroy {
  readonly greeting = HOME_GREETING;
  readonly video = HOME_VIDEO;
  readonly benefitsTitle = HOME_BENEFITS_TITLE;
  readonly benefitsButton = HOME_BENEFITS_BUTTON;
  readonly benefits = HOME_BENEFITS;
  readonly cta = HOME_CTA;
  readonly links = HOME_LINKS;
  readonly subscriptionUrl = HOME_SUBSCRIPTION_URL;

  highlights: IHomeHighlight[] = this.toHighlights(
    SHOW_GENERATIONAL_FUNDS_DEFAULT
  );
  readonly videoCover = toAssetUrl(HOME_ASSETS.videoCover);
  readonly ctaBanner = toAssetUrl(HOME_ASSETS.ctaBanner);
  readonly playIcon = toAssetUrl(HOME_ASSETS.playIcon);
  readonly externalIcon = toAssetUrl(HOME_ASSETS.externalIcon);

  showVideo: boolean = false;
  isStartingSubscription: boolean = false;
  videoEmbedUrl: SafeResourceUrl | null = null;

  @ViewChild(IonModal) private readonly videoModal?: IonModal;

  constructor(
    private readonly globalSvc: GlobalStorage,
    private readonly firebaseAuth: Auth,
    private readonly apvConfigSvc: ApvConfigService,
    private readonly subscriptionStore: SubscriptionStore,
    private readonly sanitizer: DomSanitizer,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly destroyRef: DestroyRef
  ) {
    this.setVideo(HOME_LINKS.video);
  }

  async ngOnInit(): Promise<void> {
    sessionStorage.removeItem('subscription');
    this.closeModalsOnNavigation();
    this.loadHighlights();
    this.loadVideoUrl();
    await this.storeTokenIntoGlobalStorage();
  }

  ngOnDestroy(): void {
    this.closeModals();
  }

  async goToSubscription(): Promise<void> {
    if (this.isStartingSubscription) return;

    this.isStartingSubscription = true;

    const lastStepUrl = this.subscriptionStore.lastStepUrl();

    try {
      if (lastStepUrl) {
        await this.router.navigateByUrl(lastStepUrl);
      } else {
        await this.router.navigate([this.subscriptionUrl], {
          relativeTo: this.route,
        });
      }
    } finally {
      this.isStartingSubscription = false;
    }
  }

  openLink(url: string): void {
    if (!url) return;

    window.open(url, '_blank');
  }

  openVideo(): void {
    if (!this.videoEmbedUrl) return;

    this.showVideo = true;
  }

  closeVideo(): void {
    this.showVideo = false;
  }

  private loadHighlights(): void {
    this.apvConfigSvc
      .watchShowGenerationalFunds()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((showGenerationalFunds) => {
        this.highlights = this.toHighlights(showGenerationalFunds);
      });
  }

  private toHighlights(showGenerationalFunds: boolean): IHomeHighlight[] {
    return HOME_HIGHLIGHTS.filter(
      (highlight) =>
        showGenerationalFunds || !highlight.requiresGenerationalFunds
    ).map((highlight) => ({
      ...highlight,
      icon: toAssetUrl(highlight.icon),
    }));
  }

  private loadVideoUrl(): void {
    this.apvConfigSvc
      .homeVideoUrl(HOME_LINKS.video)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((url) => this.setVideo(url));
  }

  private setVideo(url: string): void {
    const embedUrl = toYoutubeEmbedUrl(url);

    if (!embedUrl) return;

    this.videoEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
  }

  private closeModalsOnNavigation(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.closeModals());
  }

  private closeModals(): void {
    this.showVideo = false;
    void this.videoModal?.dismiss();
  }

  private async storeTokenIntoGlobalStorage(): Promise<void> {
    try {
      const firebasetoken = await (
        await this.firebaseAuth.currentUser
      )?.getIdToken();

      this.globalSvc.token = String(firebasetoken);
    } catch (error) {
      console.error('Error storing token', error);
    }
  }
}
